import cleanDeep from 'clean-deep'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import 'dayjs/locale/pt-br'
import { useBlitzStore } from './stores/BlitzStore'

dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.locale('pt-br')

export default {
  computed: {
    isBasic () {
      return useBlitzStore().user?.type === 'basic'
    }
  },
  methods: {
    authHeader () {
      const store = useBlitzStore()
      return { Authorization: 'Bearer ' + store.token }
    },
    capitalize (val) {
      if (typeof val !== 'string' || val.length === 0) return val
      return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()
    },
    clean (obj, opts = {}) {
      return cleanDeep(obj, opts)
    },
    addMonth (val, months = 1) {
      return dayjs(val)
        .add(months, 'month')
        .format('YYYY-MM-DD')
    },
    subMonth (val, months = 1) {
      return dayjs(val)
        .subtract(months, 'month')
        .format('YYYY-MM-DD')
    },
    formatISODate (val) {
      const date = typeof val === 'string' ? new Date(val) : val
      return date?.toISOString()
    },
    formatLocaleDate (val) {
      const date = typeof val === 'string' ? new Date(val) : val
      return date?.toLocaleDateString()
    },
    formatBirthDate (val) {
      if (val) {
        return dayjs(val)
          .format('DD/MM')
      } else {
        return ''
      }
    },
    formatDate (val) {
      if (val) {
        return dayjs(val)
          .format('L')
          .toLowerCase()
      } else {
        return ''
      }
    },
    formatDateTime (val) {
      if (val) {
        return dayjs(val)
          .format('DD/MM/YYYY HH:mm:ss')
          .toLowerCase()
      } else {
        return ''
      }
    },
    formatClock (val) {
      if (val) {
        return dayjs(val).format('HH:mm')
      } else {
        return null
      }
    },
    formatCreatedAt (val) {
      if (val) {
        return dayjs(val)
          .format('llll')
          .toLowerCase()
      } else {
        return ''
      }
    },
    formatUpdatedAt (val) {
      if (val) {
        return dayjs(val)
          .fromNow()
          .toLowerCase()
      } else {
        return ''
      }
    },
    formatTimer (val) {
      const number = parseInt(val, 10)
      let hours = Math.floor(number / 3600)
      let minutes = Math.floor((number - hours * 3600) / 60)
      let seconds = number - hours * 3600 - minutes * 60

      if (hours < 10) {
        hours = '0' + hours
      }

      if (minutes < 10) {
        minutes = '0' + minutes
      }

      if (seconds < 10) {
        seconds = '0' + seconds
      }

      return hours + ':' + minutes + ':' + seconds
    },
    formatCpf (val) {
      if (!val) {
        return ''
      }

      if (val.length === 11) {
        return `${val.substr(0, 3)}.${val.substr(3, 3)}.${val.substr(6, 3)}-${val.substr(9, 2)}`
      } else {
        return val
      }
    },
    formatCpfCnpj (val) {
      if (!val) {
        return ''
      }

      if (val.length === 11) {
        return `${val.substr(0, 3)}.${val.substr(3, 3)}.${val.substr(6, 3)}-${val.substr(9, 2)}`
      } else if (val.length === 14) {
        return `${val.substr(0, 2)}.${val.substr(2, 3)}.${val.substr(5, 3)}/${val.substr(8, 4)}-${val.substr(12, 2)}`
      } else {
        return val
      }
    },
    maskCpfCnpj (val) {
      if (!val) {
        return ''
      }

      if (val.length === 11) {
        return `${val.substr(0, 3)}.***.***-${val.substr(9, 2)}`
      } else if (val.length === 14) {
        return `${val.substr(0, 2)}.***.***/${val.substr(8, 4)}-${val.substr(12, 2)}`
      } else {
        return val
      }
    },
    formatPhone (val) {
      const phone = val ? val.replace(/\D+/g, '') : ''

      if (phone.length === 13) {
        return `+${phone.substr(0, 2)} (${phone.substr(2, 2)}) ${phone.substr(4, 5)}-${phone.substr(9, 4)}`
      } else if (phone.length === 11) {
        return `(${phone.substr(0, 2)}) ${phone.substr(2, 5)}-${phone.substr(7, 4)}`
      } else if (phone.length === 10) {
        return `(${phone.substr(0, 2)}) ${phone.substr(2, 4)}-${phone.substr(6, 4)}`
      } else {
        return phone
      }
    },
    formatMoney (val) {
      const formatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      })

      return formatter.format(val ?? 0)
    },
    formatMoneyK (val) {
      if (val >= 1000) {
        val = val / 1000
      }

      return this.formatMoney(val ?? 0)
    },
    getPublicPath (path) {
      if (path) {
        return path.replace(/^public/, '/storage')
      } else {
        return path
      }
    },
    truncate (val, maxlength = 255, delimiter = '...') {
      if (!val) return ''

      const length = maxlength - delimiter.length

      if (val.length > length) {
        return (
          val
            .substr(0, length)
            .trim()
            .replace(/,\s*$/, '') + delimiter
        )
      } else {
        return val
      }
    },
    validateForm (field, errors) {
      const error = errors ? errors[field] || null : null
      return !!error
    },
    getValidationMessage (field, errors) {
      const error = errors ? errors[field] || null : null
      return error ? error[0] : null
    },
    handleAxiosError (error) {
      let message
      let errors = []

      if (error.response) {
        message = error.response.data?.message ?? 'Falha na requisição'
        errors = error.response.data?.errors ?? []
      } else if (error.request) {
        message = 'Erro na solicitação: sem resposta do servidor'
      } else {
        message = error.message ?? 'Erro desconhecido'
      }

      return { message, errors }
    }
  }
}
